import GroceryList from '../../models/GroceryList';
import GroceryListItem from '../../models/GroceryListItem';
import { selectActiveList, selectActiveListRows } from './selectors';

/**
 * The active list as it is being edited (docs/local-first-food-data.md, step 2b): the GroceryList
 * and GroceryListItem models the list screen types into, kept as a draft over the food store's rows
 * and merged with them by row id each time they change.
 *
 * - A row the store has and the draft has not changed takes the store's copy, in the store's order.
 * - A row with text not yet saved (hasChanges()) keeps the draft's copy, even if the store has since
 *   lost it, until it is saved. A tick needs no such care: it is an op, applied to the store's rows
 *   until the server has it (food/applyOp.js), so the store's copy already shows it.
 * - A row not yet saved (no id, known by its instanceNumber) lives only in the draft, after the rest.
 * - While the draft is being saved, it keeps its own order, so a reorder in flight is not undone by a
 *   refresh landing before its answer. While a typed row is in that save, rows new to the draft wait
 *   for the answer too: a refresh read after the save committed holds the typed row under its new id,
 *   and only the answer ties that id to the row, by temporary_id.
 * - Otherwise a row new to the draft with the name of a row typed here and not yet saved is that row,
 *   saved by a request whose answer never arrived. The typed row takes its id rather than show twice.
 *
 * Each item stays the same object for as long as its row is on the list, so the row being typed in
 * is not remounted by a refresh and keeps its focus and text. saveGroceryList() and the reorder
 * build from the draft; the calendar slice holds it (calendar.groceryListDraft) so both reach it.
 */

const keepsDraftCopy = (item) => item.hasChanges();

/** A store row as a GroceryListItem takes it: a struck row is bought, so it has no last_bought. */
const itemData = (row, lastBoughtByName) => (row.struck ?
	{ ...row, last_bought: null } :
	{ ...row, last_bought: lastBoughtByName[String(row.name).toLowerCase()] || null });

/**
 * `draft` (a GroceryList, or null) merged with the active list `list` and its `rows` as
 * selectActiveListRows() gives them. Returns `draft` itself, changed in place, while it is a draft of
 * the same list; a new GroceryList when there was none or the list is another; null without a list.
 */
export function mergeGroceryListDraft(draft, list, rows, lastBoughtByName = {}, { keepOrder = false } = {}) {
	if(!list || list.id === null || list.id === undefined) {
		return null;
	}
	const fresh = !draft || draft.id !== Number(list.id);
	const target = fresh ?
		GroceryList.create({ id: list.id, name: list.name || '', is_active: true, purchased_on: null, created_at: list.created_at }) :
		draft;
	if(!fresh && target.name !== (list.name || '')) {
		target.name = list.name || '';
	}

	const held = new Map();
	target.items.forEach((item) => {
		if(item.id) {
			held.set(Number(item.id), item);
		}
	});
	// Typed here and not yet saved, by trimmed name, for a row new to the draft to be matched with.
	const typed = new Map();
	if(!keepOrder) {
		target.items.forEach((item) => {
			const name = String(item.name || '').trim();
			if(!item.id && name && !typed.has(name)) {
				typed.set(name, item);
			}
		});
	}
	const merged = new Map();
	rows.forEach((row) => {
		const id = Number(row.id);
		let existing = held.get(id);
		const twin = !existing && !row.struck && typed.get(String(row.name || '').trim());
		if(twin) {
			typed.delete(String(row.name).trim());
			existing = twin.update(itemData(row, lastBoughtByName));
		}
		if(!existing) {
			merged.set(id, GroceryListItem.create(itemData(row, lastBoughtByName)));
		} else {
			if(!keepsDraftCopy(existing)) {
				existing.update(itemData(row, lastBoughtByName));
			}
			merged.set(id, existing);
		}
	});

	const unsaved = target.items.filter((item) => !item.id);
	// Changed here but gone from the store: kept until its save answers.
	const orphans = target.items.filter((item) => item.id && !merged.has(Number(item.id)) && keepsDraftCopy(item));
	let saved;
	if(keepOrder) {
		const kept = target.items.filter((item) => item.id && (merged.has(Number(item.id)) || keepsDraftCopy(item)))
			.map((item) => merged.get(Number(item.id)) || item);
		const typedInSave = target.items.some((item) => !item.id && String(item.name || '').trim());
		const added = typedInSave ? [] : [...merged.values()].filter((item) => !held.has(Number(item.id)));
		saved = [...kept, ...added];
	} else {
		saved = [...merged.values(), ...orphans];
	}
	target.items = [...saved, ...unsaved];
	return target;
}

/**
 * The draft for the store's state at `minute` (see nowMinute()): calendar.groceryListDraft merged
 * with the food store's active list.
 */
export function groceryListDraftOf(state, minute) {
	const entities = state.food.entities;
	const held = state.calendar.groceryListDraft || null;
	const saving = !!held && !!state.calendar.savingGroceryListItems && state.calendar.savingGroceryListItems.has(held);
	return mergeGroceryListDraft(held, selectActiveList(entities), selectActiveListRows(entities, minute), entities.lastBoughtByName, { keepOrder: saving });
}
